import { useNavigate } from 'react-router-dom';
import type { Bid } from '../types';
import { BidStatus } from '../types';
import { CURRENCIES } from '../constants';
import { CountdownTimer } from './CountdownTimer';

type Props = {
  bid: Bid;
  currentUserId?: string;
  onEdit?: (bid: Bid) => void;
  onUpcomingExpire?: () => void;
  onLiveExpire?: () => void;
  /** History: ended auctions — resend completion PDF to creator only */
  showResendReport?: boolean;
  onResendReport?: () => void;
  resendReportLoading?: boolean;
  /** When set, bid detail "Back" returns here (e.g. `/history`) */
  returnTo?: string;
  /** Override default navigation to bid detail (e.g. subscription gate on live bids). */
  onNavigateToBid?: (bid: Bid) => void;
};

export function BidCard({
  bid,
  currentUserId,
  onEdit,
  onUpcomingExpire,
  onLiveExpire,
  showResendReport,
  onResendReport,
  resendReportLoading,
  returnTo,
  onNavigateToBid,
}: Props) {
  const isLive = bid.status === BidStatus.LIVE;
  const isUpcoming = bid.status === BidStatus.UPCOMING;
  const isEnded = bid.status === BidStatus.ENDED;
  const isCreator = bid.creatorId === currentUserId;
  const currencyObj = CURRENCIES.find((c) => c.code === bid.item.currency) || CURRENCIES[0];
  const decrementalValue = (bid.item.initialPrice * bid.decrementalPercentage) / 100;
  const descriptionText = (bid.item.description || '').trim() || 'No description';
  const createdByLabel =
    (bid.creatorCompanyName && String(bid.creatorCompanyName).trim()) ||
    (bid.creatorName && String(bid.creatorName).trim()) ||
    'System Admin';

  const statusBg = isLive ? 'bg-emerald-500' : isUpcoming ? 'bg-slate-500' : 'bg-slate-800';
  const statusLabel = isLive ? 'LIVE' : isUpcoming ? 'UPCOMING' : 'ENDED';

  const historyEndedRow = showResendReport && isEnded;
  const navigate = useNavigate();
  const detailState = returnTo ? { returnTo } : undefined;
  const goBidDetail = () => {
    if (onNavigateToBid) {
      onNavigateToBid(bid);
      return;
    }
    navigate(`/bid/${bid.id}`, { state: detailState });
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={goBidDetail}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goBidDetail();
        }
      }}
      className="mb-4 cursor-pointer rounded-2xl bg-white px-4 py-4 shadow-md outline-none ring-blue-500 focus-visible:ring-2"
      style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
    >
      {/* Header: ENDED left; Resend Report top-right (history), same color as ENDED */}
      <div
        className={`mb-3 flex w-full min-w-0 flex-row items-center gap-2 ${
          historyEndedRow && isCreator && onResendReport
            ? 'flex-nowrap justify-between'
            : 'flex-wrap'
        }`}
      >
        <div
          className={`flex shrink-0 flex-row items-center gap-1 rounded-xl px-4 py-1.5 text-[10px] font-black uppercase tracking-wider text-white ${statusBg} ${isLive ? 'animate-pulse' : ''}`}
        >
          {isLive && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
          {statusLabel}
        </div>
        {historyEndedRow && isCreator && onResendReport ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onResendReport();
            }}
            disabled={resendReportLoading}
            className="shrink-0 rounded-xl bg-slate-800 px-4 py-1.5 text-[10px] font-black uppercase tracking-wider text-white hover:bg-slate-900 disabled:opacity-50"
          >
            {resendReportLoading ? 'Sending…' : 'Resend Report'}
          </button>
        ) : null}
      </div>

      <h3 className="mb-2 text-lg font-bold text-slate-800">{bid.title}</h3>

      <div className="mb-4 flex flex-row flex-wrap items-center gap-2">
        <span className="rounded-md bg-blue-100 px-2 py-1 text-[10px] font-bold uppercase text-blue-700">
          {bid.item.category}
        </span>
        <span className="min-w-0 flex-1 text-xs leading-4 text-slate-500 line-clamp-4">{descriptionText}</span>
      </div>

      {/* Price block — matches mobile grey panel */}
      <div className="mb-4 flex flex-row justify-between gap-4 rounded-2xl bg-slate-100 p-4">
        <div>
          <p className="mb-1 text-[10px] font-black uppercase tracking-wide text-slate-500">
            {isLive ? 'Leading Bid' : 'Starting From'}
          </p>
          <p className="text-[28px] font-black leading-none text-blue-600">
            {currencyObj.symbol}
            {bid.currentLowestBid.toLocaleString()}{' '}
            <span className="text-xs font-bold text-slate-500">{bid.item.currency}</span>
          </p>
        </div>
        <div className="flex flex-col items-end border-l border-slate-300 pl-4 text-right">
          <p className="mb-1 text-[10px] font-black uppercase tracking-wide text-slate-500">Decrement</p>
          <p className="text-sm font-black text-blue-600">
            -{currencyObj.symbol}
            {decrementalValue.toLocaleString()}
          </p>
          <span className="mt-0.5 rounded px-1.5 py-0.5 text-[9px] font-bold text-blue-400 bg-blue-100">
            {bid.decrementalPercentage}%
          </span>
        </div>
      </div>

      {/* Upcoming: dashed row + countdown + Edit — matches mobile */}
      {isUpcoming && (
        <div className="mb-4 flex flex-row items-center justify-between gap-3 border-t border-dashed border-slate-300 pt-4">
          <div>
            <p className="mb-1 text-[10px] font-black uppercase tracking-wide text-slate-500">Time to start</p>
            <CountdownTimer targetDate={new Date(bid.startTime)} onExpire={onUpcomingExpire} />
          </div>
          {isCreator && onEdit ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/create/${bid.id}`);
              }}
              className="shrink-0 rounded-xl bg-slate-500 px-4 py-1.5 text-[10px] font-black uppercase tracking-wider text-white hover:opacity-95"
            >
              Edit
            </button>
          ) : null}
        </div>
      )}

      {isLive && onLiveExpire ? (
        <div className="mb-4 border-t border-dashed border-slate-300 pt-4">
          <p className="mb-1 text-[10px] font-black uppercase tracking-wide text-slate-500">Time remaining</p>
          <CountdownTimer targetDate={new Date(bid.endTime)} onExpire={onLiveExpire} />
        </div>
      ) : null}

      {/* Footer — matches mobile */}
      <div
        className={`flex flex-row items-center border-t border-slate-100 pt-4 ${isEnded ? 'justify-start' : 'justify-between'}`}
      >
        <p className="text-[10px] font-black uppercase tracking-wide text-slate-500">
          Created By: {createdByLabel}
        </p>
        {!isEnded ? (
          <span
            className={`shrink-0 rounded-xl px-5 py-2 text-xs font-black uppercase ${
              isUpcoming ? 'bg-slate-100 text-amber-600' : 'bg-blue-600 text-white'
            }`}
          >
            {isUpcoming ? '🔒 Locked' : '▶️ Enter'}
          </span>
        ) : null}
      </div>
    </div>
  );
}
